import { InvitationScreen, messageOf } from "@hakili/app-shell";
import {
  type CabinetMeAnswer,
  type CapacityDeclaredAnswer,
  invitationTokenFrom,
  isErrorEnvelope,
  type TeamAnswer,
  type TeamMember as TeamRow,
} from "@hakili/contracts";
import { formatDate, t } from "@hakili/i18n";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Connexion } from "./routes/connexion.js";
import { DeskReports } from "./routes/desk-reports.js";
import { EntryForm } from "./routes/entry.js";
import { MyTour } from "./routes/my-tour.js";
import { MonCabinet, type MonCabinetProps, type TeamMember } from "./routes/mon-cabinet.js";
import { Capacity, Fees, History, type Thursday } from "./routes/office.js";
import { type SheetAnswer, type SheetDoors, TourSheetScreen } from "./routes/tour-sheet.js";
import { chairOf, useSession } from "./session/useSession.js";
import { DESK_SCREENS, DeskShell, type DeskScreen, PhoneShell } from "./shell/ExpertShell.js";

/**
 * `round C5.0`, `C5.0.1`: **the expert app, stage 1 , the page behind the
 * link.** `/tournee/{token}` is the path the console's link names
 * (`TourService.sendSheet`, `link: "/tournee/<token>"`); the token is read
 * from the path, handed to the doors, and printed nowhere.
 *
 * `round C5-2`, `M1`: **stage 2 , the cabinet's own space around it.**
 * `/invitation/{token}` accepts an invitation (the shared screen, `00-SPINE`
 * 6.3); every other path is the space: the login until a session stands, then
 * the shell by role , the responsable's desk, the field expert's phone. The
 * link's page stays public and unchanged: a cabinet without a space still
 * reads its sheet by the link.
 */

/** The link's own shape: `/tournee/<64 hex>`. */
const TOKEN_PATH = /^\/tournee\/([a-f0-9]{64})\/?$/u;
export const tokenFrom = (pathname: string): string | null => TOKEN_PATH.exec(pathname)?.[1] ?? null;
/**
 * `FX-3`, `M3` (`S2-4`): this app's own copy of the matcher was CORRECT and is gone all the same ,
 * `bank-web`'s was not, and a property held by four copies is held by none. The name stays, because this
 * app's router and its specs read it; what it reads is the contract.
 */
export const invitationFrom = (pathname: string): string | null => invitationTokenFrom(pathname);

export class DoorRefusal extends Error {}

const refusalOf = async (answer: Response): Promise<DoorRefusal> => {
  const body: unknown = await answer.json().catch(() => null);
  return new DoorRefusal(isErrorEnvelope(body) ? body.error.message : t("common.error.connexionEchouee"));
};

/** The two public doors, plain `fetch`: no session, no token header, no client. */
export const sheetDoors = (origin: string, fetchImpl: typeof globalThis.fetch): SheetDoors => ({
  read: async (token) => {
    const answer = await fetchImpl(`${origin}/api/v1/public/tour-sheets/${token}`, {
      headers: { accept: "application/json" },
    });
    if (!answer.ok) throw await refusalOf(answer);
    return ((await answer.json()) as { sheet: SheetAnswer }).sheet;
  },
  acknowledge: async (token, version) => {
    const answer = await fetchImpl(`${origin}/api/v1/public/tour-sheets/${token}/acknowledge`, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ version }),
    });
    if (!answer.ok) throw await refusalOf(answer);
    return (await answer.json()) as { acknowledgedAt: string };
  },
  isDoorWords: (failure) => failure instanceof DoorRefusal,
});

/**
 * One row of `GET /api/v1/users`, from the contract's columns to the cabinet's own names (`GENERIC-DOOR-SWEEP`, Arc 1:
 * the row is `@hakili/contracts`' {@link TeamRow}, the same the door is typed by and the integration spec parses).
 */
const memberFrom = (row: TeamRow): TeamMember => ({
  id: row.id,
  fullName: row.full_name,
  email: row.email,
  role: row.role,
  status: row.status,
  createdAt: row.created_at,
});

/** The desk's screens this movement builds; the others are greyed with their round in the title. */
const BUILT: Readonly<Record<DeskScreen, string | null>> = {
  tour: null,
  reports: null,
  fees: null,
  cabinet: null,
};

function Space(props: {
  readonly session: ReturnType<typeof useSession>;
  readonly fullName: string;
  readonly role: string;
  readonly organisation: string;
  readonly mayInviteRoles: readonly string[];
  /** The browser's own fetch for the store's presigned PUT; the spec's recording fetch stands in. */
  readonly uploads?: typeof globalThis.fetch;
}): React.JSX.Element {
  const { session, fullName, role, organisation, mayInviteRoles, uploads } = props;
  const chair = chairOf(role);
  const [screen, setScreen] = useState<DeskScreen>("tour");
  const [members, setMembers] = useState<readonly TeamMember[] | null>(null);
  // `round C5-2`, `M4`: the fiche as the cabinet reads it, and the coming Thursdays' capacity.
  const [office, setOffice] = useState<{
    card: NonNullable<MonCabinetProps["card"]>;
    thursdays: readonly Thursday[];
  } | null>(null);
  const deconnecter = (): void => {
    void session.deconnecter();
  };

  const readTeam = useCallback(async (): Promise<void> => {
    const answer = await session.api.request<TeamAnswer>("/api/v1/users", "get");
    setMembers(answer.data.map(memberFrom));
  }, [session.api]);

  const readOffice = useCallback(async (): Promise<void> => {
    const answer = await session.api.request<CabinetMeAnswer>("/api/v1/cabinet/me", "get");
    setOffice({
      card: {
        accreditation: answer.cabinet.accreditation,
        zoneLabel: answer.cabinet.zoneLabel,
        phone: answer.cabinet.phone,
        since: formatDate(new Date(`${answer.cabinet.since}T00:00:00Z`)),
      },
      thursdays: answer.capacities,
    });
  }, [session.api]);

  useEffect(() => {
    if (chair === "responsable" && screen === "cabinet") {
      readTeam().catch(() => {
        setMembers([]);
      });
      readOffice().catch(() => {
        setOffice(null);
      });
    }
  }, [chair, screen, readTeam, readOffice]);

  const declare = useCallback(
    async (dayId: string, capacity: number): Promise<void> => {
      try {
        await session.api.request<CapacityDeclaredAnswer>("/api/v1/cabinet/capacities/{dayId}", "put", {
          params: { dayId },
          body: { capacity },
        });
      } catch (failure) {
        throw new Error(messageOf(failure, t("common.error.connexionEchouee")), { cause: failure });
      }
      await readOffice();
    },
    [session.api, readOffice],
  );

  const inviter = useCallback(
    async (input: { fullName: string; email: string; phone: string }): Promise<{ delivered: boolean }> => {
      try {
        const answer = await session.api.request<{ delivered?: unknown }>("/api/v1/users", "post", {
          body: { ...input, role: "expertTerrain" },
        });
        return { delivered: answer.delivered !== false };
      } catch (failure) {
        throw new Error(messageOf(failure, t("common.error.connexionEchouee")), { cause: failure });
      }
    },
    [session.api],
  );

  if (chair === "expert") {
    return (
      <PhoneShell fullName={fullName} deconnecter={deconnecter}>
        <MyTour
          api={session.api}
          chair="expert"
          renderEntry={(opened) =>
            opened.demandId === null ? (
              <p className="px-5 text-xs text-gray-500">{t("expert.empeche.sansDemande")}</p>
            ) : (
              <EntryForm
                api={session.api}
                demandId={opened.demandId}
                chair="expert"
                {...(uploads === undefined ? {} : { uploads })}
              />
            )
          }
        />
      </PhoneShell>
    );
  }

  return (
    <DeskShell
      fullName={fullName}
      role={role}
      screen={screen}
      onScreen={(next) => {
        if (BUILT[next] === null) setScreen(next);
      }}
      greyed={Object.fromEntries(
        DESK_SCREENS.filter((entry) => BUILT[entry.id] !== null).map((entry) => [entry.id, BUILT[entry.id]]),
      )}
      deconnecter={deconnecter}
    >
      {screen === "tour" ? (
        <MyTour
          api={session.api}
          chair="responsable"
          renderEntry={(opened) =>
            opened.demandId === null ? (
              <p className="px-5 text-xs text-gray-500">{t("expert.empeche.sansDemande")}</p>
            ) : (
              <EntryForm
                api={session.api}
                demandId={opened.demandId}
                chair="responsable"
                {...(uploads === undefined ? {} : { uploads })}
              />
            )
          }
        />
      ) : null}
      {screen === "reports" ? (
        <DeskReports
          api={session.api}
          {...(uploads === undefined ? {} : { uploads })}
          history={(relire) => <History api={session.api} onCorrectionOpened={relire} />}
        />
      ) : null}
      {screen === "fees" ? <Fees api={session.api} /> : null}
      {screen === "cabinet" ? (
        <MonCabinet
          cabinetName={organisation}
          members={members}
          mayInvite={mayInviteRoles.includes("expertTerrain")}
          inviter={inviter}
          relire={readTeam}
          {...(office === null ? {} : { card: office.card })}
        >
          {office === null ? null : <Capacity thursdays={office.thursdays} declare={declare} />}
        </MonCabinet>
      ) : null}
    </DeskShell>
  );
}

export function App(props: {
  readonly origin: string;
  readonly pathname: string;
  readonly fetchImpl?: typeof globalThis.fetch;
}): React.JSX.Element {
  const { origin, pathname, fetchImpl } = props;
  const doors = useMemo(
    () => sheetDoors(origin, fetchImpl ?? globalThis.fetch.bind(globalThis)),
    [origin, fetchImpl],
  );
  const token = tokenFrom(pathname);

  if (token !== null) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <div className="mx-auto max-w-2xl">
          <TourSheetScreen token={token} doors={doors} />
        </div>
      </div>
    );
  }
  if (pathname.startsWith("/tournee/")) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <div className="mx-auto max-w-2xl px-5 pt-5">
          <h1 className="text-xl font-semibold tracking-tight">{t("expert.feuille.titre")}</h1>
          <p role="alert" data-testid="feuille-refus" className="mt-3 text-sm text-gray-700">
            {t("expert.feuille.lienInconnu")}
          </p>
        </div>
      </div>
    );
  }
  return <SpaceApp origin={origin} pathname={pathname} {...(fetchImpl === undefined ? {} : { fetchImpl })} />;
}

/** The space: a session is opened here and never on the link's page. */
function SpaceApp(props: {
  readonly origin: string;
  readonly pathname: string;
  readonly fetchImpl?: typeof globalThis.fetch;
}): React.JSX.Element {
  const { origin, pathname, fetchImpl } = props;
  const invitation = invitationFrom(pathname);
  const session = useSession(origin, fetchImpl);
  if (invitation !== null && session.session.status !== "authenticated") {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <div className="mx-auto max-w-xl p-6">
          <InvitationScreen
            token={invitation}
            origin={origin}
            {...(fetchImpl === undefined ? {} : { fetchImpl })}
            onAccepted={(accessToken) => {
              void session.adopter(accessToken);
              globalThis.history.replaceState(null, "", "/");
            }}
          />
        </div>
      </div>
    );
  }
  if (session.session.status === "pending") {
    return <p className="p-6 text-sm text-gray-500">{t("expert.session.chargement")}</p>;
  }
  if (session.session.status === "anonymous") {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <Connexion connecter={session.connecter} motif={session.session.refusal} />
      </div>
    );
  }
  const identity = session.session.identity;
  return (
    <Space
      session={session}
      fullName={identity.fullName}
      role={identity.role}
      organisation={identity.organisation}
      mayInviteRoles={identity.mayInviteRoles}
      {...(fetchImpl === undefined ? {} : { uploads: fetchImpl })}
    />
  );
}
